import { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setCanvasZoomTargetScale } from '../../../canvas/store/canvasActions';
import {
    canvasZoomTargetScaleSelector,
    canvasZoomScaleSelector,
    canvasZoomTranslationPxSelector,
} from '../../../canvas/store/canvasZoomSelector';
import { getX, getY } from '../../../../common/maths/geometry/rect';
import { boardTemplatePickerContext } from '../context/boardTemplatePickerContext';
import { scale } from '../../../../common/maths/geometry/point';
import getGridSize from '../../../utils/grid/gridSizeSelector';

import { CANVAS_VIEWPORT_ID } from '../../../canvas/utils/canvasConstants';

const DEFAULT_TEMPLATE_PREVIEW_ZOOM_SCALE = 0.46;
const DEFAULT_TEMPLATE_PREVIEW_OFFSET = { x: 0, y: 0 };

/**
 * Hook to handle zooming to fit the selected template
 */
export default () => {
    const { selectedTemplateId, templates, isCopyingTemplate } = useContext(boardTemplatePickerContext);

    const targetScale = useSelector(canvasZoomTargetScaleSelector);
    const currentZoomScale = useSelector(canvasZoomScaleSelector);
    const currentZoomTranslationPx = useSelector(canvasZoomTranslationPxSelector);
    const gridSize = useSelector(getGridSize);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!selectedTemplateId) return;

        const template = templates[selectedTemplateId];
        if (!template) return;

        const canvasViewportNode = document.getElementById(CANVAS_VIEWPORT_ID);
        if (!canvasViewportNode) return;

        const { mobilePreviewZoomScale, mobilePreviewOffset } = template;

        const mobilePreviewOffsetGUUnscaled = mobilePreviewOffset ?? DEFAULT_TEMPLATE_PREVIEW_OFFSET;
        const mobilePreviewOffsetPxUnscaled = scale(gridSize)(mobilePreviewOffsetGUUnscaled);
        const mobilePreviewOffsetPxScaled = scale(currentZoomScale)(mobilePreviewOffsetPxUnscaled);

        const canvasViewportNodeRect = canvasViewportNode.getBoundingClientRect();

        canvasViewportNode.scrollLeft = getX(currentZoomTranslationPx) + getX(mobilePreviewOffsetPxScaled);
        canvasViewportNode.scrollTop = getY(currentZoomTranslationPx) + getY(mobilePreviewOffsetPxScaled);

        dispatch(
            setCanvasZoomTargetScale({
                scale: mobilePreviewZoomScale || DEFAULT_TEMPLATE_PREVIEW_ZOOM_SCALE,
                shouldAnimate: false,
                eventPosition: {
                    x: canvasViewportNodeRect.left,
                    y: canvasViewportNodeRect.top,
                },
                disableZoomFeedback: true,
            }),
        );

        // When the sheet is closed, reset the canvas zoom target scale
        return () => {
            if (isCopyingTemplate || targetScale === 1) return;

            dispatch(setCanvasZoomTargetScale({ scale: 1, shouldAnimate: false, disableZoomFeedback: true }));
        };
    }, [selectedTemplateId]);
};
